import { useEffect, useRef, useState } from 'preact/hooks';
import { monthNameSv } from '@kff/engine';
import type { Api } from '../api.js';
import { peek } from '../api.js';
import { dm, dmSv, ddm, ddmSv, h, weekLabel, weekLabelSv } from '../format.js';
import { texts, useLang, type Lang } from '../i18n.js';
import { RoleIcon } from '../shell.js';
import { navigate } from '../router.js';
import { Avatar, Block, ErrorBox, LoadError, Loading, PageHead, Pill, toast, trapTab } from '../ui.js';
import { dayLabel, PT, type PlanBlockView } from '../planTime.js';

export interface TeamCell {
  week: string;
  weekNo: number;
  demandH: number;
  capacityH: number | null;
  pct: number | null;
  tone: string;
  outsideCapacityH: number;
  unbookedH: number;
  days?: { day: string; hours: number; outsideCapacityH: number }[];
}
export type TeamGroup = 'editors' | 'camera' | 'producers' | 'others' | 'outside';
export interface TeamRow {
  personKey: string;
  name: string;
  sub: string;
  group?: TeamGroup;
  kind?: 'employee' | 'freelancer' | null;
  /** the person's working roles (editor, camera …), for the role icons */
  roles?: string[];
  personId?: string | null;
  availability?: 'sent' | 'partial' | 'none' | null;
  cells: TeamCell[];
}
export interface TeamView {
  title: string;
  source: { label: string; failed: boolean };
  readOnly: { title: string; body: string };
  weeks: string[];
  rows: TeamRow[];
  notScheduled: { month: string | null; label: string; projects: { projectId: string; text: string; packageNote: string; status: string; pastMonth: boolean }[] }[];
  waiting: { projectId: string; projectName: string; personKey: string; round: number; expectedBy: string; hours: number; unbooked: boolean }[];
  checkInMonday: { projectId: string; projectName: string; text: string }[];
  notCounted: { excluded: { projectId: string; projectName: string; why: string }[]; shares: { text: string }[] } | null;
  suggestions: { summary: string; consultantBoardUrl: string; items: { key: string; text: string }[] } | null;
  availability: { month: string; text: string } | null;
}

const GROUP_KEYS: TeamGroup[] = ['editors', 'camera', 'producers', 'others'];
const MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** Team's own texts (English stays the default; Swedish for people who picked it). */
const TX = texts(
  {
    unbooked: 'not marked as booked',
    groups: { editors: 'Editing', camera: 'Camera', producers: 'Producers', others: 'Others' } as Record<string, string>,
    tone: { crit: 'over capacity', warn: 'nearly full', miss: 'no availability' } as Record<string, string>,
    over: 'Over',
    and: 'and',
    more: (n: number) => `${n} more`,
    weekLabel,
    dm,
    ddm,
    monthLong: (m: string) => MONTH_LONG[+m.slice(5, 7) - 1] ?? '',
    weekShort: (n: number | string) => `W${n}`,
    free: (x: string) => `${x} free`,
    notAvailable: (x: string) => `${x} · not available`,
    notAvailableShort: 'N/A',
    missShort: 'Unspecified',
    overBy: (x: string) => `+${x} over`,
    zeroNoAvail: '0 h, no availability',
    zeroOf: (cap: string) => `0 h of ${cap}`,
    weekendOutside: (x: string) => `+ ${x} weekend · outside capacity`,
    incl: (x: string) => `incl. ${x} not marked as booked`,
    addToList: 'Add to the consultant list',
    addToListInfo: 'Hours of people who are not on the consultant list in Monday are not counted on anyone. Add them in Monday and the next sync counts them. The Hub only suggests; it never writes to Monday.',
    dismissed: 'Suggestion dismissed',
    openList: 'Open the consultant list in Monday',
    dismiss: 'Dismiss',
    weeks: (a: number | string, b: number | string) => `Weeks ${a}–${b}`,
    readOnly: 'Read-only',
    readEvery: ' Monday is read every 15 minutes from Video Management.',
    showRole: 'Show role',
    all: 'All',
    summary: 'Summary',
    overCapacity: 'Over capacity',
    overWho: (who: string, a: number | string, b: number | string) => `${who} · weeks ${a}–${b}`,
    nobodyOver: 'nobody this week or next',
    freeNext: 'Free next 2 weeks',
    mostWith: (name: string, x: string) => `most with ${name} (${x})`,
    allBooked: 'everyone is booked',
    availMissing: 'Availability missing',
    of: (a: number, b: number) => `${a} of ${b}`,
    freelancersIn: 'freelancers in these weeks',
    waiting: 'Waiting for feedback',
    rounds: (x: string) => `rounds · ${x} when they land`,
    noRounds: 'no rounds out with clients',
    hoursPerWeek: 'Hours per week',
    hoursPerPersonWeek: 'Hours per person and week',
    cardLabel: (name: string, n: number, what: string) => `${name}, week ${n}: ${what}`,
    nothingBooked: 'nothing booked',
    hNoAvail: (x: string) => `${x}, no availability`,
    hBookedNotAvail: (x: string) => `${x} booked, not available`,
    colours: 'Colours',
    under85: 'Under 85 %',
    over100: 'Over 100 %',
    noAvailYet: 'No availability yet',
    selectWeek: 'Select a week to see where the hours come from',
    outside: (n: number) => `Not in the Hub yet (${n}) · their hours have nothing to compare with`,
    lists: 'Lists',
    tabs: { waiting: 'Waiting for feedback', unscheduled: 'Not scheduled', check: 'Check in Monday', notcounted: 'Not counted' } as Record<Tab, string>,
    waitingInfo: 'Revisions get hours only when Monday shows "Feedback received" for the round. Then the round\'s hours go in the 3 working days after, at most 8 h a day.',
    project: 'Project',
    round: 'Round',
    person: 'Person',
    expectedBy: 'Expected by',
    hoursWhenLands: 'Hours when it lands',
    unscheduledInfo: 'Projects without dates show under their month and are not counted in any week until they get dates.',
    pastMonth: 'Past month – no dates',
    checkInfo: 'Dates or labels in Monday that the rules could not use as they are. Fix them in Monday – the Hub only reads.',
    onlyBooked: ' (only Booked and Preliminary count)',
    now: 'Now',
    teamLoad: 'Team load',
    weekOf: (name: string, n: number) => `${name}, week ${n}: `,
    close: 'Close',
    noAvailWeek: 'no availability for this week',
    bookedNotAvail: (x: string) => `${x} booked · not available this week (0 h)`,
    xOfY: (a: string, b: string) => `${a} of ${b}`,
    dow: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    loading: 'Loading…',
    nothingThisWeek: 'Nothing booked this week.',
    kind: (k: string) => k,
    weekendOutsideShort: ' · weekend, outside capacity',
    changeInMonday: 'Change dates and people in Monday. The Hub shows it within 15 minutes.',
    allWeeksFor: (name: string) => `All weeks for ${name}`,
    backTeam: '← Team',
    personSub: (x: string) => `${x} in the next 6 weeks · source Monday · read-only`,
    weeksLabel: 'Weeks',
    whereFrom: 'Where the hours come from',
    outsideCapacity: 'outside capacity',
    todo: 'To do',
    readFailed: 'The last read from Monday failed.',
    readFailedRest: ' The numbers are from the last successful read.',
    notSent: (n: number) => `${n === 1 ? 'has' : 'have'} not sent availability for all of these weeks`,
    openAvailFor: (m: string) => `Open availability for ${m}`,
    nProjects: (n: number) => `${n} project${n === 1 ? '' : 's'}`,
    couldNotUse: (n: number) => `${n === 1 ? 'has' : 'have'} dates or labels in Monday the Hub could not use`,
    checkInMonday: 'Check in Monday'
  },
  {
    unbooked: 'inte markerat som bokat',
    groups: { editors: 'Redigering', camera: 'Kamera', producers: 'Producenter', others: 'Övriga' },
    tone: { crit: 'över kapacitet', warn: 'nästan fullt', miss: 'ingen tillgänglighet' },
    over: 'Över',
    and: 'och',
    more: (n: number) => `${n} till`,
    weekLabel: weekLabelSv,
    dm: dmSv,
    ddm: ddmSv,
    monthLong: (m: string) => monthNameSv(m),
    weekShort: (n: number | string) => `v. ${n}`,
    free: (x: string) => `${x} tillgängligt`,
    notAvailable: (x: string) => `${x} · inte tillgänglig`,
    notAvailableShort: 'N/A',
    missShort: 'Ospecificerat',
    overBy: (x: string) => `+${x} över`,
    zeroNoAvail: '0 h, ingen tillgänglighet',
    zeroOf: (cap: string) => `0 h av ${cap}`,
    weekendOutside: (x: string) => `+ ${x} helg · utanför kapaciteten`,
    incl: (x: string) => `varav ${x} inte markerat som bokat`,
    addToList: 'Lägg till i konsultlistan',
    addToListInfo: 'Timmar för personer som inte finns i konsultlistan i Monday räknas inte på någon. Lägg till dem i Monday så räknas de vid nästa synk. Hubben föreslår bara – den skriver aldrig till Monday.',
    dismissed: 'Förslaget avfärdat',
    openList: 'Öppna konsultlistan i Monday',
    dismiss: 'Avfärda',
    weeks: (a: number | string, b: number | string) => `Vecka ${a}–${b}`,
    readOnly: 'Skrivskyddat',
    readEvery: ' Monday läses var 15:e minut från Video Management.',
    showRole: 'Visa roll',
    all: 'Alla',
    summary: 'Sammanfattning',
    overCapacity: 'Över kapacitet',
    overWho: (who: string, a: number | string, b: number | string) => `${who} · vecka ${a}–${b}`,
    nobodyOver: 'ingen den här veckan eller nästa',
    freeNext: 'Tillgängligt kommande 2 veckor',
    mostWith: (name: string, x: string) => `mest hos ${name} (${x})`,
    allBooked: 'alla är bokade',
    availMissing: 'Tillgänglighet saknas',
    of: (a: number, b: number) => `${a} av ${b}`,
    freelancersIn: 'frilansare de här veckorna',
    waiting: 'Väntar på feedback',
    rounds: (x: string) => `rundor · ${x} när de kommer in`,
    noRounds: 'inga rundor ute hos kunder',
    hoursPerWeek: 'Timmar per vecka',
    hoursPerPersonWeek: 'Timmar per person och vecka',
    cardLabel: (name: string, n: number, what: string) => `${name}, vecka ${n}: ${what}`,
    nothingBooked: 'inget bokat',
    hNoAvail: (x: string) => `${x}, ingen tillgänglighet`,
    hBookedNotAvail: (x: string) => `${x} bokat, inte tillgänglig`,
    colours: 'Färger',
    under85: 'Under 85 %',
    over100: 'Över 100 %',
    noAvailYet: 'Ingen tillgänglighet än',
    selectWeek: 'Välj en vecka för att se var timmarna kommer från',
    outside: (n: number) => `Inte i Hubben än (${n}) · deras timmar har inget att jämföras med`,
    lists: 'Listor',
    tabs: { waiting: 'Väntar på feedback', unscheduled: 'Inte schemalagt', check: 'Kolla i Monday', notcounted: 'Räknas inte' },
    waitingInfo: 'Revideringar får timmar först när Monday visar ”Feedback received” för rundan. Då läggs rundans timmar på de 3 arbetsdagarna efter, högst 8 h per dag.',
    project: 'Projekt',
    round: 'Runda',
    person: 'Person',
    expectedBy: 'Väntas senast',
    hoursWhenLands: 'Timmar när den kommer in',
    unscheduledInfo: 'Projekt utan datum visas under sin månad och räknas inte in i någon vecka förrän de får datum.',
    pastMonth: 'Passerad månad – inga datum',
    checkInfo: 'Datum eller etiketter i Monday som reglerna inte kunde använda som de är. Rätta dem i Monday – Hubben läser bara.',
    onlyBooked: ' (bara Bokad och Preliminär räknas)',
    now: 'Nu',
    teamLoad: 'Teamets belastning',
    weekOf: (name: string, n: number) => `${name}, vecka ${n}: `,
    close: 'Stäng',
    noAvailWeek: 'ingen tillgänglighet den här veckan',
    bookedNotAvail: (x: string) => `${x} bokat · inte tillgänglig den här veckan (0 h)`,
    xOfY: (a: string, b: string) => `${a} av ${b}`,
    dow: ['mån', 'tis', 'ons', 'tors', 'fre', 'lör', 'sön'],
    loading: 'Laddar…',
    nothingThisWeek: 'Inget bokat den här veckan.',
    kind: (k: string) => ({ edit: 'redigering', rev: 'revidering', shoot: 'inspelning' } as Record<string, string>)[k] ?? k,
    weekendOutsideShort: ' · helg, utanför kapaciteten',
    changeInMonday: 'Ändra datum och personer i Monday. Hubben visar det inom 15 minuter.',
    allWeeksFor: (name: string) => `Alla veckor för ${name}`,
    backTeam: '← Team',
    personSub: (x: string) => `${x} de kommande 6 veckorna · källa Monday · skrivskyddat`,
    weeksLabel: 'Veckor',
    whereFrom: 'Var timmarna kommer från',
    outsideCapacity: 'utanför kapaciteten',
    todo: 'Att göra',
    readFailed: 'Den senaste läsningen från Monday misslyckades.',
    readFailedRest: ' Siffrorna är från den senaste lyckade läsningen.',
    notSent: () => 'har inte skickat in tillgänglighet för alla de här veckorna',
    openAvailFor: (m: string) => `Öppna tillgänglighet för ${m}`,
    nProjects: (n: number) => `${n} projekt`,
    couldNotUse: () => 'har datum eller etiketter i Monday som Hubben inte kunde använda',
    checkInMonday: 'Kolla i Monday'
  }
);
type T = (typeof TX)['en'];

/** What a week looks like: missing availability is its own state, so amber only ever means 85–100 %. */
export function look(c: TeamCell): 'crit' | 'warn' | 'ok' | 'miss' | 'empty' {
  if (c.capacityH === null) return c.demandH ? 'miss' : 'empty';
  if (!c.demandH) return 'empty';
  return c.tone === 'crit' ? 'crit' : c.tone === 'warn' ? 'warn' : 'ok';
}
/** Load as text; booked hours against 0 h capacity (e.g. 0 % availability that week) has no percent – it is just over. */
export const pctText = (c: TeamCell, sep = ' ', lang: Lang = 'en'): string => (c.pct === null ? TX[lang].over : `${c.pct}${sep}%`);
/** Bar fill: a week over with no capacity is a full bar. */
export const barPct = (c: TeamCell): number => (c.pct === null ? 100 : Math.min(100, c.pct));
const free = (c: TeamCell) => (c.capacityH === null ? 0 : Math.max(0, c.capacityH - c.demandH));
const round1 = (n: number) => Math.round(n * 10) / 10;


/**
 * One person-week: percent and a bar, hours under it. Over capacity is told calmly (owner 2026-10-16: "Över" in red
 * everywhere was too dramatic): the percent and the bar turn red and the hours over are written out – never colour
 * alone – but the cell gets no fill and no badge. Booked against 0 h capacity shows the booked hours, "not available".
 */
export function Cell(p: { c: TeamCell }) {
  const { c } = p;
  const lang = useLang();
  const tx = TX[lang];
  const t = look(c);
  const label = tx.tone[t];
  const off = t === 'crit' && c.pct === null;
  const main = t === 'miss' || off ? h(c.demandH) : t === 'empty' ? '–' : pctText(c, ' ', lang);
  const sub = t === 'miss' ? tx.missShort : t === 'empty' ? (c.capacityH ? tx.free(h(c.capacityH)) : '') : off ? tx.notAvailableShort : `${h(c.demandH)} / ${h(c.capacityH)}`;
  const overH = t === 'crit' && !off && c.capacityH !== null ? round1(c.demandH - c.capacityH) : 0;
  return (
    <span class={`wk tone-${t}${off ? ' is-off' : ''}`}>
      <span class="wk-top">
        <span class="wk-main" aria-hidden={t === 'empty' ? 'true' : undefined}>
          {main}
        </span>
        {t === 'empty' ? <span class="sr-only">{c.capacityH === null ? tx.zeroNoAvail : tx.zeroOf(h(c.capacityH))}</span> : null}
        {label ? <span class="sr-only"> {label}</span> : null}
        {sub && !(t === 'empty' && c.capacityH === null) ? <span class="wk-sub">{sub}</span> : null}
      </span>
      {t === 'empty' ? null : (
        <span class="wk-bar" aria-hidden="true">
          <i style={`width:${t === 'miss' || off ? 0 : barPct(c)}%`} />
        </span>
      )}
      {overH > 0 ? <span class="wk-over">{tx.overBy(h(overH))}</span> : null}
      {c.outsideCapacityH ? <span class="wk-note">{tx.weekendOutside(h(c.outsideCapacityH))}</span> : null}
      {c.unbookedH ? <span class="wk-note">{tx.incl(h(c.unbookedH))}</span> : null}
    </span>
  );
}

/** Add to the consultant list (owner answer 3, 2026-10-07): admin only; the Hub never writes to Monday. */
export function Suggestions(p: { api: Api; s: TeamView['suggestions']; reload: () => void }) {
  const t = TX[useLang()];
  if (!p.s || !p.s.items.length) return null;
  return (
    <Block title={t.addToList} info={t.addToListInfo}>
      <p class="small">
        <strong>{p.s.summary.split(' · ')[0]}</strong> · {p.s.summary.split(' · ').slice(1).join(' · ')}
      </p>
      <ul class="list">
        {p.s.items.map((x) => (
          <li class="list-row">
            <span>{x.text}</span>
            <SuggestionActions api={p.api} url={p.s!.consultantBoardUrl} k={x.key} reload={p.reload} />
          </li>
        ))}
      </ul>
    </Block>
  );
}

function SuggestionActions(p: { api: Api; url: string; k: string; reload: () => void }) {
  const t = TX[useLang()];
  const dismiss = async () => {
    await p.api.post(`/api/workload/suggestions/${encodeURIComponent(p.k)}/dismiss`);
    p.reload();
    toast({
      text: t.dismissed,
      undo: async () => {
        await p.api.del(`/api/workload/suggestions/${encodeURIComponent(p.k)}/dismiss`);
        p.reload();
      }
    });
  };
  return (
    <span class="cluster">
      <a class="btn btn-sm" href={p.url} target="_blank" rel="noopener noreferrer">
        {t.openList}
      </a>
      <button type="button" class="btn btn-ghost btn-sm" onClick={() => void dismiss()}>
        {t.dismiss}
      </button>
    </span>
  );
}

/** The four numbers a planner looks for first, from the same rows as the grid. */
export function teamStats(v: Pick<TeamView, 'rows' | 'waiting'>) {
  const rows = v.rows.filter((r) => r.group !== 'outside');
  const over = rows.filter((r) => r.cells.slice(0, 2).some((c) => look(c) === 'crit'));
  const freeBy = rows.map((r) => ({ name: r.name, h: round1(r.cells.slice(0, 2).reduce((a, c) => a + free(c), 0)) })).sort((a, b) => b.h - a.h);
  const freelancers = rows.filter((r) => r.kind === 'freelancer');
  const missing = freelancers.filter((r) => r.availability && r.availability !== 'sent');
  return {
    over,
    freeH: round1(freeBy.reduce((a, x) => a + x.h, 0)),
    mostFree: freeBy[0] && freeBy[0].h > 0 ? freeBy[0] : null,
    freelancers: freelancers.length,
    missing,
    waitingH: round1(v.waiting.reduce((a, w) => a + w.hours, 0))
  };
}

const first = (n: string) => n.split(' ')[0];
const names = (rs: { name: string }[], t: T = TX.en) => {
  const n = rs.map((r) => first(r.name));
  return n.length <= 2 ? n.join(` ${t.and} `) : `${n.slice(0, 2).join(', ')} ${t.and} ${t.more(n.length - 2)}`;
};

type Tab = 'waiting' | 'unscheduled' | 'check' | 'notcounted';

/** Team › Workload with Monday as the source (UX-1060) – read-only. */
export function Team(p: { api: Api }) {
  const lang = useLang();
  const t = TX[lang];
  const [v, setV] = useState<TeamView | null>(() => peek(p.api, '/api/workload/team') ?? null);
  const [error, setError] = useState<unknown>(null);
  const [group, setGroup] = useState<TeamGroup | 'all'>('all');
  const [tab, setTab] = useState<Tab | null>(null);
  const [open, setOpen] = useState<{ row: TeamRow; cell: TeamCell } | null>(null);
  const load = () => p.api.get<TeamView>('/api/workload/team').then(setV, setError);
  useEffect(() => {
    void load();
  }, []);
  if (error) return <LoadError error={error} />;
  if (!v) return <Loading />;

  const s = teamStats(v);
  const known = v.rows.filter((r) => r.group !== 'outside');
  const outside = v.rows.filter((r) => r.group === 'outside');
  const groups = GROUP_KEYS.map((key) => ({ key, label: t.groups[key]! })).filter((g) => known.some((r) => (r.group ?? 'others') === g.key));
  const shown = known.filter((r) => group === 'all' || (r.group ?? 'others') === group);
  const w0 = v.rows[0]?.cells[0]?.weekNo ?? '';
  const w1 = v.rows[0]?.cells.at(-1)?.weekNo ?? '';
  const missMonth = v.availability?.month;
  const missLabel = missMonth ? t.monthLong(missMonth) : '';

  const tabs: { key: Tab; label: string; n: number }[] = [
    { key: 'waiting' as Tab, label: t.tabs.waiting, n: v.waiting.length },
    { key: 'unscheduled' as Tab, label: t.tabs.unscheduled, n: v.notScheduled.reduce((n, m) => n + m.projects.length, 0) },
    { key: 'check' as Tab, label: t.tabs.check, n: v.checkInMonday.length },
    { key: 'notcounted' as Tab, label: t.tabs.notcounted, n: v.notCounted ? v.notCounted.excluded.length + v.notCounted.shares.length : 0 }
  ].filter((x) => x.n);
  const active = tabs.find((x) => x.key === tab)?.key ?? tabs[0]?.key ?? null;
  const showTab = (k: Tab) => {
    setTab(k);
    document.getElementById('team-lists')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const nameOf = (key: string) => v.rows.find((r) => r.personKey === key)?.name ?? '';

  return (
    <div class="screen team">
      <PageHead title={v.title} sub={t.weeks(w0, w1)} />
      <div class="team-meta">
        <span class={`live${v.source.failed ? ' live--failed' : ''}`}>{v.source.label}</span>
        <details class="ro-tip">
          <summary>{t.readOnly}</summary>
          <div class="ro-pop">
            <strong>{v.readOnly.title}</strong>
            <p>
              {v.readOnly.body}
              {t.readEvery}
            </p>
          </div>
        </details>
        {groups.length > 1 ? (
          <div class="chips" role="group" aria-label={t.showRole} data-tour="team-roles">
            {[{ key: 'all' as const, label: t.all }, ...groups].map((g) => (
              <button type="button" class="chip" aria-pressed={group === g.key} onClick={() => setGroup(g.key)}>
                {g.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <section class="stats" aria-label={t.summary} data-tour="team-stats">
        <div class={`stat${s.over.length ? ' stat--crit' : ''}`}>
          <span class="stat-k">{t.overCapacity}</span>
          <span class="stat-v">{s.over.length}</span>
          <span class="stat-d">{s.over.length ? t.overWho(names(s.over, t), w0, v.rows[0]?.cells[1]?.weekNo ?? w0) : t.nobodyOver}</span>
        </div>
        <div class="stat">
          <span class="stat-k">{t.freeNext}</span>
          <span class="stat-v">{h(s.freeH)}</span>
          <span class="stat-d">{s.mostFree ? t.mostWith(first(s.mostFree.name)!, h(s.mostFree.h)) : t.allBooked}</span>
        </div>
        {s.freelancers ? (
          <div class={`stat${s.missing.length ? ' stat--warn' : ''}`}>
            <span class="stat-k">{t.availMissing}</span>
            <span class="stat-v">{t.of(s.missing.length, s.freelancers)}</span>
            <span class="stat-d">{t.freelancersIn}</span>
          </div>
        ) : null}
        <div class="stat">
          <span class="stat-k">{t.waiting}</span>
          <span class="stat-v">{v.waiting.length}</span>
          <span class="stat-d">{v.waiting.length ? t.rounds(h(s.waitingH)) : t.noRounds}</span>
        </div>
      </section>

      <Todo v={v} api={p.api} reload={load} missing={s.missing} missLabel={missLabel} missMonth={missMonth} showCheck={() => showTab('check')} />

      <section class="panel" aria-labelledby="team-grid-h" data-tour="team-grid">
        <h2 class="sr-only" id="team-grid-h">
          {t.hoursPerWeek}
        </h2>
        <div class="tbl-wrap team-grid-wrap" tabIndex={0} role="region" aria-label={t.hoursPerPersonWeek}>
          <Grid weeks={v.weeks} rows={shown} total onOpen={(row, cell) => setOpen({ row, cell })} />
        </div>
        <div class="team-cards">
          {groupRows(shown, t).map(([g, rs]) => (
            <>
              <div class="tc-group">{g}</div>
              {rs.map((r) => (
                <div class="tc">
                  <Who r={r} />
                  <div class="tc-weeks">
                    {r.cells.map((c, i) => {
                      const k = look(c);
                      return (
                        <button type="button" class={`tc-w tone-${k}${k === 'crit' && c.pct === null ? ' is-off' : ''}${i === 0 ? ' now' : ''}`} onClick={() => setOpen({ row: r, cell: c })} aria-label={t.cardLabel(r.name, c.weekNo, k === 'empty' ? t.nothingBooked : k === 'miss' ? t.hNoAvail(h(c.demandH)) : c.pct === null ? t.hBookedNotAvail(h(c.demandH)) : `${c.pct} %`)}>
                          <span class="tc-col">
                            <i style={`height:${k === 'miss' || k === 'empty' || c.pct === null ? 0 : barPct(c)}%`} />
                          </span>
                          <b>{k === 'empty' ? '–' : k === 'miss' || c.pct === null ? `${round1(c.demandH)}h` : pctText(c, '', lang)}</b>
                          <small>{t.weekShort(c.weekNo)}</small>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </>
          ))}
        </div>
        <div class="legend" aria-label={t.colours} data-tour="team-legend">
          <span>
            <i class="sw sw-ok" />
            {t.under85}
          </span>
          <span>
            <i class="sw sw-warn" />
            85–100 %
          </span>
          <span>
            <i class="sw sw-crit" />
            {t.over100}
          </span>
          <span>
            <i class="sw sw-miss" />
            {t.noAvailYet}
          </span>
          <span class="muted">{t.selectWeek}</span>
        </div>
        {outside.length ? (
          <details class="outside">
            <summary>{t.outside(outside.length)}</summary>
            <div class="tbl-wrap" tabIndex={0} role="region" aria-label={t.outside(outside.length)}>
              <Grid weeks={v.weeks} rows={outside} onOpen={(row, cell) => setOpen({ row, cell })} />
            </div>
          </details>
        ) : null}
      </section>

      {active ? (
        <section class="panel" id="team-lists" aria-label={t.lists}>
          <div class="seg" role="tablist">
            {tabs.map((x) => (
              <button type="button" role="tab" id={`tab-${x.key}`} aria-selected={active === x.key} aria-controls="team-tabpanel" class="seg-tab" onClick={() => setTab(x.key)}>
                {x.label}
                <span class="seg-n">{x.n}</span>
              </button>
            ))}
          </div>
          <div role="tabpanel" id="team-tabpanel" aria-labelledby={`tab-${active}`} class="seg-panel">
            {active === 'waiting' ? (
              <>
                <p class="blk-info">{t.waitingInfo}</p>
                <div class="tbl-wrap" tabIndex={0} role="region" aria-label={t.tabs.waiting}>
                  <table class="tbl">
                    <thead>
                      <tr>
                        <th scope="col">{t.project}</th>
                        <th scope="col">{t.round}</th>
                        <th scope="col">{t.person}</th>
                        <th scope="col">{t.expectedBy}</th>
                        <th scope="col">{t.hoursWhenLands}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {v.waiting.map((x) => (
                        <tr>
                          <td>
                            <strong>{x.projectName}</strong>
                            {x.unbooked ? <span class="flag"> {t.unbooked}</span> : null}
                          </td>
                          <td class="num">{x.round}</td>
                          <td>{nameOf(x.personKey)}</td>
                          <td class="num">{t.ddm(x.expectedBy)}</td>
                          <td class="num">{h(x.hours)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}
            {active === 'unscheduled' ? (
              <>
                <p class="blk-info">{t.unscheduledInfo}</p>
                {v.notScheduled.map((m) => (
                  <div>
                    <p>
                      <strong>{m.label}</strong>
                    </p>
                    <ul class="why">
                      {m.projects.map((x) => (
                        <li>
                          {x.text} <span class="small muted">· {x.packageNote} · {x.status}</span> {x.pastMonth ? <span class="flag">{t.pastMonth}</span> : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </>
            ) : null}
            {active === 'check' ? (
              <>
                <p class="blk-info">{t.checkInfo}</p>
                <ul class="why">
                  {v.checkInMonday.map((f) => (
                    <li>
                      <strong>{f.projectName}</strong> · <span class="flag">{f.text}</span>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
            {active === 'notcounted' && v.notCounted ? (
              <ul class="why">
                {v.notCounted.excluded.map((x) => (
                  <li>
                    {x.projectName} · {x.why}
                    {t.onlyBooked}
                  </li>
                ))}
                {v.notCounted.shares.map((x) => (
                  <li>{x.text}</li>
                ))}
              </ul>
            ) : null}
          </div>
        </section>
      ) : null}

      {open ? <WeekPanel api={p.api} row={open.row} cell={open.cell} onClose={() => setOpen(null)} /> : null}
    </div>
  );
}

function groupRows(rows: TeamRow[], t: T): [string, TeamRow[]][] {
  return GROUP_KEYS.map((g) => [t.groups[g], rows.filter((r) => (r.group ?? 'others') === g)] as [string, TeamRow[]]).filter(([, rs]) => rs.length);
}

const ROLE_WORD = { sv: { editor: 'Redigering', camera: 'Kamera' }, en: { editor: 'Editing', camera: 'Camera' } } as const;

function Who(p: { r: TeamRow }) {
  const lang = useLang();
  const missing = p.r.availability === 'none' || p.r.availability === 'partial';
  // who edits and who films, at a glance (owner 2026-10-20)
  const icons = (['editor', 'camera'] as const).filter((k) => p.r.roles?.includes(k));
  return (
    <span class="who">
      <Avatar name={p.r.name} decorative />
      <span class="who-t">
        <a href={`/team/${encodeURIComponent(p.r.personKey)}`} onClick={(e) => (e.preventDefault(), navigate(`/team/${encodeURIComponent(p.r.personKey)}`))}>
          {p.r.name}
        </a>
        {icons.length ? (
          <span class="role-set who-roles">
            {icons.map((k) => (
              <RoleIcon key={k} role={k} label={ROLE_WORD[lang][k]} />
            ))}
          </span>
        ) : null}
        <span class={`who-sub${missing ? ' who-sub--miss' : ''}`}>{p.r.sub}</span>
      </span>
    </span>
  );
}

/** The week grid: equal week columns, the name column stays put when the grid scrolls, rows grouped by role. */
function Grid(p: { weeks: string[]; rows: TeamRow[]; total?: boolean; onOpen: (r: TeamRow, c: TeamCell) => void }) {
  const t = TX[useLang()];
  const counted = p.rows.filter((r) => r.group !== 'outside');
  const grouped = p.total ? groupRows(p.rows, t) : [['', p.rows] as [string, TeamRow[]]];
  return (
    <table class="tbl team-grid">
      <colgroup>
        <col class="col-who" />
        {p.weeks.map(() => (
          <col />
        ))}
      </colgroup>
      <thead>
        <tr>
          <th scope="col">{t.person}</th>
          {p.weeks.map((w, i) => {
            const [wk, dates] = t.weekLabel(w).split(' · ');
            return (
              <th scope="col" class={i === 0 ? 'now' : undefined}>
                <span class="wk-h">
                  {wk}
                  {i === 0 ? <span class="now-tag">{t.now}</span> : null}
                </span>
                <span class="wk-d">{dates}</span>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {p.total && counted.length ? (
          <tr class="row-total">
            <th scope="row">{t.teamLoad}</th>
            {p.weeks.map((_, i) => {
              const cs = counted.map((r) => r.cells[i]!).filter((c) => c && c.capacityH !== null);
              const dem = cs.reduce((a, c) => a + c.demandH, 0);
              const cap = cs.reduce((a, c) => a + (c.capacityH ?? 0), 0);
              const pct = cap ? Math.round((dem / cap) * 100) : null;
              const k = pct === null ? 'empty' : pct > 100 ? 'crit' : pct >= 85 ? 'warn' : 'ok';
              return (
                <td class={i === 0 ? 'now' : undefined}>
                  <span class={`wk tone-${k}`}>
                    <span class="wk-top">
                      <span class="wk-main">{pct === null ? '–' : `${pct} %`}</span>
                      <span class="wk-sub">{cap ? t.free(h(Math.max(0, round1(cap - dem)))) : ''}</span>
                    </span>
                    <span class="wk-bar" aria-hidden="true">
                      <i style={`width:${Math.min(100, pct ?? 0)}%`} />
                    </span>
                  </span>
                </td>
              );
            })}
          </tr>
        ) : null}
        {grouped.map(([g, rs]) => (
          <>
            {g ? (
              <tr class="row-group">
                <th scope="colgroup" colSpan={p.weeks.length + 1}>
                  {g} <span>{rs.length}</span>
                </th>
              </tr>
            ) : null}
            {rs.map((r) => (
              <tr>
                <th scope="row">
                  <Who r={r} />
                </th>
                {r.cells.map((c, i) => (
                  <td class={[i === 0 ? 'now' : '', `load-${look(c)}`].join(' ').trim()}>
                    <button type="button" class="wk-btn" onClick={() => p.onOpen(r, c)}>
                      <span class="sr-only">{t.weekOf(r.name, c.weekNo)}</span>
                      <Cell c={c} />
                    </button>
                  </td>
                ))}
              </tr>
            ))}
          </>
        ))}
      </tbody>
    </table>
  );
}

interface PersonDetailView {
  name: string;
  totalH: number;
  weeks: TeamCell[];
  lines: { projectName: string; kind: string; reason: string; flag?: string; outsideCapacity?: boolean; days: { day: string; hours: number }[] }[];
  waiting: { reason: string; projectName: string }[];
  /** the time the person planned themselves (owner 2026-11-03); older APIs leave it out */
  plan?: PlanBlockView[];
}

const inWeek = (week: string, day: string) => {
  const end = new Date(`${week}T00:00:00Z`);
  end.setUTCDate(end.getUTCDate() + 7);
  return day >= week && day < end.toISOString().slice(0, 10);
};

/** Side panel for one person-week: hours per day and the projects they come from. */
export function WeekPanel(p: { api: Api; row: TeamRow; cell: TeamCell; onClose: () => void }) {
  const lang = useLang();
  const tx = TX[lang];
  const [d, setD] = useState<PersonDetailView | null>(null);
  const [error, setError] = useState<unknown>(null);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setD(null);
    void p.api.get<PersonDetailView>(`/api/workload/person/${encodeURIComponent(p.row.personKey)}`).then(setD, setError);
  }, [p.row.personKey]);
  useEffect(() => {
    const back = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => (e.key === 'Escape' ? p.onClose() : ref.current && trapTab(ref.current, e));
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      back?.focus();
    };
  }, []);
  const c = p.cell;
  const t = look(c);
  const lines = (d?.lines ?? [])
    .map((l) => ({ ...l, h: round1(l.days.filter((x) => inWeek(c.week, x.day)).reduce((a, x) => a + x.hours, 0)) }))
    .filter((l) => l.h > 0)
    .sort((a, b) => b.h - a.h);
  return (
    <div class="sheet-backdrop" role="presentation" onClick={(e) => e.target === e.currentTarget && p.onClose()}>
      <div class="sheet" role="dialog" aria-modal="true" aria-labelledby="sheet-h" tabIndex={-1} ref={ref}>
        <button type="button" class="sheet-x btn btn-ghost btn-sm" onClick={p.onClose} aria-label={tx.close}>
          ✕
        </button>
        <div class="who">
          <Avatar name={p.row.name} decorative />
          <span class="who-t">
            <strong id="sheet-h">{p.row.name}</strong>
            <span class="who-sub">{tx.weekLabel(c.week)}</span>
          </span>
        </div>
        <div class={`sheet-big tone-${t}`}>
          <b>{t === 'miss' || t === 'empty' ? h(c.demandH) : pctText(c, ' ', lang)}</b>
          <span class="muted">{t === 'miss' ? tx.noAvailWeek : c.capacityH === null ? '' : c.pct === null ? tx.bookedNotAvail(h(c.demandH)) : tx.xOfY(h(c.demandH), h(c.capacityH))}</span>
        </div>
        {c.days?.length ? (
          <div class="sheet-days">
            {c.days.map((x, i) => (
              <div class={`sheet-day${i > 4 ? ' sheet-day--we' : ''}`}>
                {tx.dow[i]}
                <b>{x.hours || x.outsideCapacityH ? round1(x.hours + x.outsideCapacityH) : '–'}</b>
              </div>
            ))}
          </div>
        ) : null}
        {error ? <ErrorBox error={error} /> : !d ? <p class="muted small">{tx.loading}</p> : null}
        {d && !lines.length ? <p class="muted small">{tx.nothingThisWeek}</p> : null}
        {lines.length ? (
          <ul class="sheet-lines">
            {lines.map((l) => (
              <li>
                <span class="sheet-line-t">
                  <strong>{l.projectName}</strong>
                  <span class="small muted">
                    {tx.kind(l.kind)}
                    {l.outsideCapacity ? tx.weekendOutsideShort : ''}
                    {l.flag ? ` · ${l.flag}` : ''}
                  </span>
                </span>
                <span class="sheet-line-h">{h(l.h)}</span>
              </li>
            ))}
          </ul>
        ) : null}
        <p class="small muted">{tx.changeInMonday}</p>
        <PlanList blocks={(d?.plan ?? []).filter((b) => inWeek(c.week, b.day))} />
        <a
          class="btn btn-sm"
          href={`/team/${encodeURIComponent(p.row.personKey)}`}
          onClick={(e) => (e.preventDefault(), p.onClose(), navigate(`/team/${encodeURIComponent(p.row.personKey)}`))}
        >
          {tx.allWeeksFor(first(p.row.name)!)}
        </a>
      </div>
    </div>
  );
}

/**
 * The time the person planned themselves (owner 2026-11-03: producers see the blocks). Shown apart from the hours above:
 * a plan is never busy time, so a meeting can still be booked over it.
 */
export function PlanList(p: { blocks: PlanBlockView[] }) {
  const lang = useLang();
  const t = PT[lang];
  if (!p.blocks.length) return null;
  return (
    <div class="pt-team">
      <h3 class="label">{t.whoPlanned}</h3>
      <ul class="pt-list">
        {p.blocks.map((b) => (
          <li>
            {dayLabel(b.day, lang)} {b.start}–{b.end}
            <span class="muted">
              {' · '}
              {b.project}
              {b.client ? `, ${b.client}` : ''}
            </span>
          </li>
        ))}
      </ul>
      <p class="small muted pt-foot">{t.notBusy}</p>
    </div>
  );
}

/** Drill-down: "Where the hours come from" for one person. */
export function PersonDetail(p: { api: Api; personKey: string }) {
  const t = TX[useLang()];
  const [v, setV] = useState<PersonDetailView | null>(null);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    void p.api.get<PersonDetailView>(`/api/workload/person/${encodeURIComponent(p.personKey)}`).then(setV, setError);
  }, [p.personKey]);
  if (error) return <LoadError error={error} />;
  if (!v) return <Loading />;
  return (
    <div class="screen team">
      <p>
        <a class="back-link" href="/team" onClick={(e) => (e.preventDefault(), navigate('/team'))}>
          {t.backTeam}
        </a>
      </p>
      <PageHead title={v.name} sub={t.personSub(h(v.totalH))} />
      <section class="panel" aria-label={t.weeksLabel}>
        <div class="person-weeks">
          {v.weeks.map((c, i) => (
            <div class={`pw load-${look(c)}${i === 0 ? ' now' : ''}`}>
              <span class="wk-h">
                {t.weekShort(c.weekNo)}
                {i === 0 ? <span class="now-tag">{t.now}</span> : null}
              </span>
              <Cell c={c} />
            </div>
          ))}
        </div>
      </section>
      <Block title={t.whereFrom}>
        <ul class="sheet-lines">
          {v.lines.map((l) => (
            <li>
              <span class="sheet-line-t">
                <strong>{l.projectName}</strong>
                <span class="small muted">
                  {l.reason}
                  {l.flag ? <span class="flag"> {l.flag}</span> : null}
                </span>
                <span class="small muted">{l.days.map((x) => `${t.dm(x.day)} ${h(x.hours)}`).join(' · ')}</span>
              </span>
              <span class="sheet-line-h">
                {h(round1(l.days.reduce((a, x) => a + x.hours, 0)))}
                {l.outsideCapacity ? <Pill tone="neutral">{t.outsideCapacity}</Pill> : null}
              </span>
            </li>
          ))}
          {v.waiting.map((w) => (
            <li>
              <span class="sheet-line-t">
                <strong>{w.projectName}</strong>
                <em class="small muted">{w.reason}</em>
              </span>
            </li>
          ))}
        </ul>
      </Block>
      {v.plan?.length ? (
        <section class="panel pt-panel">
          <PlanList blocks={v.plan.filter((b) => b.day >= new Date().toISOString().slice(0, 10))} />
        </section>
      ) : null}
    </div>
  );
}

/** Things someone can act on now, above the grid: missing availability, people outside the consultant list, dates to fix. */
function Todo(p: { v: TeamView; api: Api; reload: () => void; missing: TeamRow[]; missLabel: string; missMonth: string | undefined; showCheck: () => void }) {
  const { v } = p;
  const t = TX[useLang()];
  const items = v.suggestions?.items ?? [];
  if (!v.source.failed && !p.missing.length && !items.length && !v.checkInMonday.length) return null;
  return (
    <section class="todo" aria-label={t.todo}>
      {v.source.failed ? (
        <div class="todo-row">
          <span class="dot dot--crit" aria-hidden="true" />
          <span>
            <strong>{t.readFailed}</strong>
            {t.readFailedRest}
          </span>
        </div>
      ) : null}
      {p.missing.length ? (
        <div class="todo-row">
          <span class="dot dot--warn" aria-hidden="true" />
          <span>
            <strong>{names(p.missing, t)}</strong> {t.notSent(p.missing.length)}
          </span>
          {p.missMonth ? (
            <a class="btn btn-sm" href={`/team/availability?month=${p.missMonth}`} onClick={(e) => (e.preventDefault(), navigate(`/team/availability?month=${p.missMonth}`))}>
              {t.openAvailFor(p.missLabel)}
            </a>
          ) : null}
        </div>
      ) : null}
      {items.map((x) => (
        <div class="todo-row">
          <span class="dot" aria-hidden="true" />
          <span>{x.text}</span>
          <SuggestionActions api={p.api} url={v.suggestions!.consultantBoardUrl} k={x.key} reload={p.reload} />
        </div>
      ))}
      {v.checkInMonday.length ? (
        <div class="todo-row">
          <span class="dot dot--warn" aria-hidden="true" />
          <span>
            <strong>{t.nProjects(v.checkInMonday.length)}</strong> {t.couldNotUse(v.checkInMonday.length)}
          </span>
          <button type="button" class="btn btn-sm" onClick={p.showCheck}>
            {t.checkInMonday}
          </button>
        </div>
      ) : null}
    </section>
  );
}
